import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import Input from '../../general/Input/Input';
import Button from '../../general/Button/Button';
import './LoginRegisterReset.css';
import { createSingleUser } from '../../../store/actions/userActions';
import { validateFullName, validateEmail, validatePassword } from '../../../utils/ValidationLoginRegisterReset';

const Register = () => {
    const dispatch = useDispatch();
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: ''
    });

    const navigate = useNavigate();

    const handleFullNameChange = (e) => {
        const value = e.target.value;
        setFullName(value);

        const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/;

        if (value.length < 3) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                fullName: "O nome completo precisa ter no mínimo 3 caracteres."
            }));
        } else if (!nameRegex.test(value)) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                fullName: "O nome completo não pode conter números ou caracteres especiais."
            }));
        } else {
            setErrors((prevErrors) => ({
                ...prevErrors,
                fullName: ''
            }));
        }
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Regex para validar o formato do e-mail

        if (value.length > 255) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                email: "E-mail muito longo! O limite é 255 caracteres."
            }));
        } else if (!emailRegex.test(value) && value.length > 0) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                email: "Formato de e-mail inválido! Certifique-se de que tenha o formato correto (ex: usuario@dominio.com)."
            }));
        } else {
            setErrors((prevErrors) => ({
                ...prevErrors,
                email: ''
            }));
        }
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);

        const minLength = 8;
        const hasUpperCase = /[A-Z]/.test(value);
        const hasNumber = /\d/.test(value);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

        if (value.length < minLength) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                password: "A senha precisa ter pelo menos 8 caracteres."
            }));
        } else if (!hasUpperCase) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                password: "A senha precisa ter pelo menos uma letra maiúscula."
            }));
        } else if (!hasNumber) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                password: "A senha precisa ter pelo menos um número."
            }));
        } else if (!hasSpecialChar) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                password: "A senha precisa ter pelo menos um caractere especial (ex: @,#,$,...)."
            }));
        } else {
            setErrors((prevErrors) => ({
                ...prevErrors,
                password: ''
            }));
        }

        if (confirmPassword && confirmPassword !== value) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                confirmPassword: "As senhas não coincidem."
            }));
        } else {
            setErrors((prevErrors) => ({
                ...prevErrors,
                confirmPassword: ''
            }));
        }
    };

    const handleConfirmPasswordChange = (e) => {
        const value = e.target.value;
        setConfirmPassword(value);

        if (password && value !== password) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                confirmPassword: "As senhas não coincidem."
            }));
        } else {
            setErrors((prevErrors) => ({
                ...prevErrors,
                confirmPassword: ''
            }));
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        let formIsValid = true;
        let newErrors = { fullName: '', email: '', password: '', confirmPassword: '' };

        // Validar o nome completo, email, senha e confirmação de senha
        const fullNameError = validateFullName(fullName);
        if (fullNameError) {
            formIsValid = false;
            newErrors.fullName = fullNameError;
        }

        const emailError = validateEmail(email);
        if (emailError) {
            formIsValid = false;
            newErrors.email = emailError;
        }

        const passwordError = validatePassword(password);
        if (passwordError) {
            formIsValid = false;
            newErrors.password = passwordError;
        }

        const confirmPasswordError = password !== confirmPassword ? "As senhas não coincidem." : "";
        if (confirmPasswordError) {
            formIsValid = false;
            newErrors.confirmPassword = confirmPasswordError;
        }

        setErrors(newErrors);

        if (formIsValid) {
            try {
                let response;
                let userName = fullName;
                let userEmail = email;
                let userPassword = password;
                let isAdmin = false;
                let approved = false
                let selectedRole = 'client'
                let selectedDashboardsFilter = []
                let selectedGroups = []
                // Criação de novo usuário
                response = await dispatch(
                    createSingleUser({ userName, userEmail, userPassword, isAdmin, approved, selectedRole, selectedDashboardsFilter, selectedGroups })
                ).unwrap();

                setFullName('')
                setEmail('')
                setPassword('')
                setConfirmPassword('')
                setErrors({
                    fullName: '',
                    email: '',
                    password: '',
                    confirmPassword: ''
                })

                navigate("/dashboard");
            } catch (error) {
                console.error("Erro ao salvar Usuário:", error);
            }
        }
    };

    return (
        <div className="containerMain">
            <div className="formContainer">
                <div className="textCenter">
                    <h1 className="heading">Bem-vindo ao AnalyticsBeta!</h1>
                    <p className="subheading">Crie sua conta e comece a explorar.</p>
                </div>

                <Helmet>
                    <title>Register</title>
                </Helmet>

                <form onSubmit={handleSubmit}>
                    <div className="inputGroup">
                        <Input
                            type="text"
                            placeholder="Seu nome completo"
                            value={fullName}
                            onChange={handleFullNameChange}
                            error={errors.fullName}
                        />
                        {errors.fullName && <p className="errorText">{errors.fullName}</p>}

                        <Input
                            type="email"
                            placeholder="example@example.com"
                            value={email}
                            onChange={handleEmailChange}
                            error={errors.email}
                        />
                        {errors.email && <p className="errorText">{errors.email}</p>}

                        <Input
                            type="password"
                            placeholder="*******"
                            value={password}
                            onChange={handlePasswordChange}
                            error={errors.password}
                        />
                        {errors.password && <p className="errorText">{errors.password}</p>}

                        <Input
                            type="password"
                            placeholder="Confirme sua senha"
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            error={errors.confirmPassword}
                        />
                        {errors.confirmPassword && <p className="errorText">{errors.confirmPassword}</p>}
                    </div>

                    <Button type="submit" className='btn-margin btn-primary' variant="primary" fullWidth>
                        Criar Conta
                    </Button>

                    <hr className="divider" />

                    <div className="textCenter">
                        <p className="text-gray-600">
                            Já tem uma conta?
                            <Link to="/" className="linkLogin">Fazer login</Link>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Register;
